import React, { useEffect, useRef, useState } from 'react';

import './Creative.scss';
import { formatCell, formatMetric, getTooltip } from './utils';
import Tooltip from '../../../components/Tooltip';
import {
  useCreativeAnalyticsGet,
  useCreativeAnalyticsSet,
} from '../../CreativeAnalytics/contexts';
import { useViewSettings } from '../../../selectors';
import { formatMetricLabel } from '../../CreativeAnalytics/utils/formatMetricLabel';
import {
  findPercentDifference,
  toCurrency,
  toPercent,
  toPercent_2,
} from '../../../utils/numbers';
import { truncate } from '../../../utils/truncate';
import { TooltipIcon } from '../../../components/Tooltip/Tooltip';
import { PeriodComparison } from '../PeriodComparison';
import { useCreativePerformanceState } from '../context';
import { useSearchParams } from '../../../components/Hooks/search-params';
import { useOverflowActive } from '../../../hooks/useOverflowActive';
import RobotIcon from '../icons/RobotIcon';
import { useViewGroupContext } from '../../ViewGroup/context';
import { NOOM_CUSTOM_METRICS } from '../../CreativeAnalytics/PerformanceFrequency/constants';
import { useAllVisualsLabel } from '../../CreativeAnalytics/selectors';
import { CopyableText } from '../../../components/CopyableText';

export const Creative = ({
  visual,
  columns = [],
  metadata,
  metadataFields,
  rank,
  showRankings,
  formatMetric,
  columnOptions,
  handleCreateManualTag,
  hasIterations,
  isOrganicView,
  metricsLookup = [],
}) => {
  const { getParams } = useSearchParams();
  const { thumbnails_only, display_ad_names } = getParams(
    'thumbnails_only',
    'display_ad_names'
  ) || {
    thumbnails_only: '',
    display_ad_names: '',
  };
  const [display, setDisplay] = useState<
    'Scores' | 'Data' | 'Attributes' | 'Info'
  >('Data');
  const { primary_metric, selectedTab } = useCreativeAnalyticsGet();

  const adNameWithLength = visual.ad_names?.find((visual) => visual?.length);
  const adNamesDisplay = adNameWithLength ?? visual._id;
  let DisplayComp = null;

  switch (display) {
    case 'Scores':
      DisplayComp = Scores;
      break;
    case 'Data':
      DisplayComp = MetricData;
      break;
    case 'Attributes':
      DisplayComp = Attributes;
      break;
    case 'Info':
      DisplayComp = InfoDisplay;
      break;
    default:
      DisplayComp = Scores;
  }

  return (
    <div className="dna-creative">
      <div className="dna-creative__top-banner">
        {visual.isTopSpending && !isOrganicView && <TopSpendingIndicator />}
        {visual.isTopPerforming && (
          <TopPerformingIndicator {...{ primary_metric }} />
        )}
      </div>

      {hasIterations && (
        <div className="iterations-robot">
          <RobotIcon />
        </div>
      )}
      <Visual
        visual={visual}
        rank={rank}
        showRankings={showRankings}
        thumbnails_only={thumbnails_only}
      />
      {display_ad_names && (
        <p className="dna-creative__ad-name visual-cell">
          {`${adNamesDisplay}`}
          {visual.ad_names?.length > 1 ? (
            <sup style={{ top: '10px', right: '10px' }}>
              {visual.ad_names.length}
            </sup>
          ) : null}
        </p>
      )}
      {/* <Toggle display={display} setDisplay={setDisplay} /> */}
      <div className="dna-creative-details">
        <DisplayComp
          visual={visual}
          metadataFields={metadataFields}
          columns={columns}
          formatMetric={formatMetric}
          columnOptions={columnOptions}
          handleCreateManualTag={handleCreateManualTag}
          metricsLookup={metricsLookup}
        />
      </div>
    </div>
  );
};

export const TopSpendingIndicator = () => {
  return (
    <div className="dna-creative__top-banner__box spend">
      <span className="trophy-icon-wrapper">
        {/* <img src={require('./trophy.png')} /> */}
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.46155 13C3.46155 12.4477 3.90926 12 4.46155 12H10.4519C10.853 12 11.2152 12.2397 11.3722 12.6088V12.6088C11.4784 12.8588 11.4784 13.1412 11.3722 13.3912V13.3912C11.2152 13.7603 10.853 14 10.4519 14H4.46155C3.90926 14 3.46155 13.5523 3.46155 13V13Z"
            fill="#9ECBFF"
          />
          <path
            d="M3.46155 2C3.46155 0.89543 4.35698 0 5.46155 0H9.53847C10.643 0 11.5385 0.895431 11.5385 2V6.57915C11.5385 7.04972 11.3725 7.50522 11.0699 7.86554L10.3256 8.75162C10.0635 9.0636 9.71242 9.28809 9.31928 9.39507L7.66478 9.84527C7.29359 9.94628 6.90115 9.93809 6.5345 9.82169L5.37023 9.45209C4.92005 9.30917 4.53497 9.01154 4.28321 8.61191L3.76936 7.79629C3.56826 7.47708 3.46155 7.10749 3.46155 6.73022V2Z"
            fill="#9ECBFF"
          />
          <path
            d="M2.7907 1.71875C2.7907 1.26291 2.99284 0.825738 3.35264 0.50341C3.71245 0.181082 4.20046 0 4.7093 0H10.2907C10.7995 0 11.2875 0.181082 11.6474 0.50341C12.0072 0.825738 12.2093 1.26291 12.2093 1.71875V1.875H13.7791C14.453 1.875 15 2.365 15 2.96875V4.84375C15 5.54596 14.6896 6.21961 14.1367 6.71738C13.5838 7.21515 12.8334 7.49653 12.0495 7.5C11.8032 8.31897 11.2875 9.05256 10.5694 9.60534C9.85129 10.1581 8.96411 10.5045 8.02326 10.5994V11.875H9.94186C10.5432 11.875 11.12 12.089 11.5452 12.4699C11.9704 12.8509 12.2093 13.3675 12.2093 13.9063V14.2188C12.2093 14.65 11.8186 15 11.3372 15H3.66279C3.1814 15 2.7907 14.65 2.7907 14.2188V13.9063C2.7907 13.3675 3.02959 12.8509 3.45482 12.4699C3.88004 12.089 4.45678 11.875 5.05814 11.875H6.97674V10.5994C6.03581 10.5044 5.14855 10.158 4.43045 9.60509C3.71234 9.05219 3.19665 8.31846 2.95047 7.49937C2.16673 7.49591 1.4164 7.21461 0.863522 6.71697C0.31064 6.21934 0.000174843 5.54585 0 4.84375V2.96875C0 2.365 0.546977 1.875 1.22093 1.875H2.7907V1.71875ZM2.7907 2.8125H1.22093C1.17467 2.8125 1.13031 2.82896 1.0976 2.85826C1.06489 2.88757 1.04651 2.92731 1.04651 2.96875V4.84375C1.04633 5.27309 1.22553 5.68693 1.54884 6.00386C1.87214 6.32079 2.31615 6.51784 2.79349 6.55625C2.7916 6.50627 2.79067 6.45626 2.7907 6.40625V2.8125ZM12.2065 6.55625C12.6838 6.51784 13.1279 6.32079 13.4512 6.00386C13.7745 5.68693 13.9537 5.27309 13.9535 4.84375V2.96875C13.9535 2.92731 13.9351 2.88757 13.9024 2.85826C13.8697 2.82896 13.8253 2.8125 13.7791 2.8125H12.2093V6.40625C12.2093 6.45625 12.2084 6.50625 12.2065 6.55625ZM4.7093 0.9375C4.22791 0.9375 3.83721 1.2875 3.83721 1.71875V6.40625C3.83721 6.83715 3.93195 7.26383 4.11602 7.66193C4.3001 8.06003 4.56989 8.42175 4.91002 8.72644C5.25014 9.03114 5.65392 9.27283 6.09831 9.43773C6.5427 9.60263 7.019 9.6875 7.5 9.6875C7.981 9.6875 8.4573 9.60263 8.90169 9.43773C9.34608 9.27283 9.74986 9.03114 10.09 8.72644C10.4301 8.42175 10.6999 8.06003 10.884 7.66193C11.068 7.26383 11.1628 6.83715 11.1628 6.40625V1.71875C11.1628 1.2875 10.7721 0.9375 10.2907 0.9375H4.7093ZM3.83721 13.9063V14.0625H11.1628V13.9063C11.1628 13.6162 11.0342 13.338 10.8052 13.1329C10.5762 12.9277 10.2657 12.8125 9.94186 12.8125H5.05814C4.73433 12.8125 4.42378 12.9277 4.19481 13.1329C3.96584 13.338 3.83721 13.6162 3.83721 13.9063Z"
            fill="black"
          />
        </svg>
      </span>

      <span>Top Spending</span>
    </div>
  );
};

export const TopPerformingIndicator = ({ primary_metric }) => {
  return (
    <div className="dna-creative__top-banner__box performance">
      <span className="trophy-icon-wrapper">
        {/* <img src={require('./trophy.png')} /> */}
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.46155 13C3.46155 12.4477 3.90926 12 4.46155 12H10.4519C10.853 12 11.2152 12.2397 11.3722 12.6088V12.6088C11.4784 12.8588 11.4784 13.1412 11.3722 13.3912V13.3912C11.2152 13.7603 10.853 14 10.4519 14H4.46155C3.90926 14 3.46155 13.5523 3.46155 13V13Z"
            fill="#9ECBFF"
          />
          <path
            d="M3.46155 2C3.46155 0.89543 4.35698 0 5.46155 0H9.53847C10.643 0 11.5385 0.895431 11.5385 2V6.57915C11.5385 7.04972 11.3725 7.50522 11.0699 7.86554L10.3256 8.75162C10.0635 9.0636 9.71242 9.28809 9.31928 9.39507L7.66478 9.84527C7.29359 9.94628 6.90115 9.93809 6.5345 9.82169L5.37023 9.45209C4.92005 9.30917 4.53497 9.01154 4.28321 8.61191L3.76936 7.79629C3.56826 7.47708 3.46155 7.10749 3.46155 6.73022V2Z"
            fill="#9ECBFF"
          />
          <path
            d="M2.7907 1.71875C2.7907 1.26291 2.99284 0.825738 3.35264 0.50341C3.71245 0.181082 4.20046 0 4.7093 0H10.2907C10.7995 0 11.2875 0.181082 11.6474 0.50341C12.0072 0.825738 12.2093 1.26291 12.2093 1.71875V1.875H13.7791C14.453 1.875 15 2.365 15 2.96875V4.84375C15 5.54596 14.6896 6.21961 14.1367 6.71738C13.5838 7.21515 12.8334 7.49653 12.0495 7.5C11.8032 8.31897 11.2875 9.05256 10.5694 9.60534C9.85129 10.1581 8.96411 10.5045 8.02326 10.5994V11.875H9.94186C10.5432 11.875 11.12 12.089 11.5452 12.4699C11.9704 12.8509 12.2093 13.3675 12.2093 13.9063V14.2188C12.2093 14.65 11.8186 15 11.3372 15H3.66279C3.1814 15 2.7907 14.65 2.7907 14.2188V13.9063C2.7907 13.3675 3.02959 12.8509 3.45482 12.4699C3.88004 12.089 4.45678 11.875 5.05814 11.875H6.97674V10.5994C6.03581 10.5044 5.14855 10.158 4.43045 9.60509C3.71234 9.05219 3.19665 8.31846 2.95047 7.49937C2.16673 7.49591 1.4164 7.21461 0.863522 6.71697C0.31064 6.21934 0.000174843 5.54585 0 4.84375V2.96875C0 2.365 0.546977 1.875 1.22093 1.875H2.7907V1.71875ZM2.7907 2.8125H1.22093C1.17467 2.8125 1.13031 2.82896 1.0976 2.85826C1.06489 2.88757 1.04651 2.92731 1.04651 2.96875V4.84375C1.04633 5.27309 1.22553 5.68693 1.54884 6.00386C1.87214 6.32079 2.31615 6.51784 2.79349 6.55625C2.7916 6.50627 2.79067 6.45626 2.7907 6.40625V2.8125ZM12.2065 6.55625C12.6838 6.51784 13.1279 6.32079 13.4512 6.00386C13.7745 5.68693 13.9537 5.27309 13.9535 4.84375V2.96875C13.9535 2.92731 13.9351 2.88757 13.9024 2.85826C13.8697 2.82896 13.8253 2.8125 13.7791 2.8125H12.2093V6.40625C12.2093 6.45625 12.2084 6.50625 12.2065 6.55625ZM4.7093 0.9375C4.22791 0.9375 3.83721 1.2875 3.83721 1.71875V6.40625C3.83721 6.83715 3.93195 7.26383 4.11602 7.66193C4.3001 8.06003 4.56989 8.42175 4.91002 8.72644C5.25014 9.03114 5.65392 9.27283 6.09831 9.43773C6.5427 9.60263 7.019 9.6875 7.5 9.6875C7.981 9.6875 8.4573 9.60263 8.90169 9.43773C9.34608 9.27283 9.74986 9.03114 10.09 8.72644C10.4301 8.42175 10.6999 8.06003 10.884 7.66193C11.068 7.26383 11.1628 6.83715 11.1628 6.40625V1.71875C11.1628 1.2875 10.7721 0.9375 10.2907 0.9375H4.7093ZM3.83721 13.9063V14.0625H11.1628V13.9063C11.1628 13.6162 11.0342 13.338 10.8052 13.1329C10.5762 12.9277 10.2657 12.8125 9.94186 12.8125H5.05814C4.73433 12.8125 4.42378 12.9277 4.19481 13.1329C3.96584 13.338 3.83721 13.6162 3.83721 13.9063Z"
            fill="black"
          />
        </svg>
      </span>
      <span>Best {formatMetric(primary_metric)}</span>
    </div>
  );
};

const getToggleTooltip = (display) => {
  if (display === 'Data' || display === 'Attributes') {
    return 'Toggle columns in the "Edit Columns" dropdown to show data.';
  }

  return null;
};

const Toggle = ({ display, setDisplay }) => {
  const opts = ['Scores', 'Data', 'Attributes', 'Info'];
  const selectedIndex = opts.findIndex((opt) => opt === display) ?? 0;
  const tooltip = getToggleTooltip(display);

  return (
    <div className="dna-creative-toggle">
      <button
        onClick={() =>
          setDisplay(
            selectedIndex > 0 ? opts[selectedIndex - 1] : opts[opts.length - 1]
          )
        }
      >
        <i className="fa-solid fa-angle-left"></i>
      </button>
      <span>
        {display}
        {tooltip && (
          <TooltipIcon
            iconStyle={{ marginLeft: '0.5rem', transform: 'translateY(1px)' }}
            content={tooltip}
          />
        )}
      </span>
      <button
        onClick={() =>
          setDisplay(
            selectedIndex < opts.length - 1 ? opts[selectedIndex + 1] : opts[0]
          )
        }
      >
        <i className="fa-solid fa-angle-right"></i>
      </button>
    </div>
  );
};

const Visual = ({ visual, rank, showRankings, thumbnails_only }) => {
  const { setParams } = useCreativeAnalyticsSet();
  let VisualDisplay = null;

  const handleClick = () => setParams({ rc: visual?._id ?? '' });

  if (visual.visual_type === 'image') {
    VisualDisplay = (
      <img
        className="dna-creative__img"
        src={visual.asset_url}
        alt={visual._id}
        onClick={handleClick}
      />
    );
  } else if (visual.visual_type === 'video') {
    if (thumbnails_only) {
      VisualDisplay = (
        <img
          className="dna-creative__img"
          src={visual.thumbnail_url}
          alt={visual._id}
          onClick={handleClick}
        />
      );
    } else {
      VisualDisplay = (
        <video
          key={visual.asset_url}
          autoPlay={false}
          loop
          muted
          plays-inline="true"
          controls
          onClick={handleClick}
        >
          <source src={visual.asset_url} type="video/mp4" />
        </video>
      );
    }
  }

  return (
    <div className="img-wrapper">
      {thumbnails_only && visual.visual_type === 'video' && (
        <i
          style={{
            position: 'absolute',
            top: '1rem',
            left: '1rem',
          }}
          className="fa-solid fa-video"
        ></i>
      )}
      <Tooltip
        content={
          <div>
            <p>Used in the following ads:</p>
            <ul
              className="element-details__list"
              style={{ paddingLeft: '0.5rem' }}
            >
              {visual.ad_names.length
                ? visual.ad_names.map((ad_name) => (
                    <li className="element-details__ad-name">{ad_name}</li>
                  ))
                : null}
            </ul>
          </div>
        }
      >
        {VisualDisplay}
      </Tooltip>
      {showRankings && <div className="rank">{rank}</div>}
    </div>
  );
};

const CustomScore = ({ visual, custom_performance_scores }) => {
  const { custom_performance_score } = visual;
  const highest =
    custom_performance_scores.scores.reduce((result, score) => {
      if (score.score > result) {
        result = score.score;
      }

      return result;
    }, 0) ?? 0;
  const shouldInvert = custom_performance_scores.preferLowerScore;

  const score = shouldInvert
    ? (highest - custom_performance_score + 1) / highest
    : custom_performance_score / highest;

  let color = '';
  const percent = score * 100;

  if (percent === 100) {
    color = '#0FBDC3';
  } else if (percent >= 75) {
    color = '#78E154';
  } else if (percent >= 40) {
    color = '#FFB543';
  } else if (percent >= 40) {
    color = '#FF7D54';
  } else {
    color = '#FF3096';
  }

  return (
    <div className="custom-score-card">
      <p className="custom-score-card__text">Performance Score</p>

      <div style={{ background: color }} className="custom-score-card__score">
        {custom_performance_score}
      </div>
    </div>
  );
};

const Scores = ({ visual, columns = [] }) => {
  const { spend_quintile, primary_metric_quintile, fitness_score } = visual;
  const { isOrganicView, custom_performance_scores } = useViewSettings();
  const { primary_metric } = useCreativeAnalyticsGet();
  const allDisabled = !columns.some((c) =>
    ['fitness_score', 'primary_metric_quintile', 'spend_quintile'].includes(c)
  );

  if (custom_performance_scores?.scores?.length) {
    return (
      <CustomScore
        visual={visual}
        custom_performance_scores={custom_performance_scores}
      />
    );
  }

  return (
    <div className="dna-creative-scores">
      {allDisabled && (
        <p className="dna-creative-data__msg">
          Enable Fitness Score, Performance Quintile, or Spend Quintile columns
          to show scores.
        </p>
      )}
      <ScoreSection
        title={<p>Overall Score</p>}
        tooltip={getTooltip('fitness_score', visual, primary_metric)}
        scoreDisplay={`${fitness_score}/10`}
        score={fitness_score / 10}
        disable={!columns?.includes('fitness_score') || isOrganicView}
      />
      <ScoreSection
        title={<p>Performance Quintile </p>}
        subtitle={
          <span style={{ color: '#7a85a3' }}>
            {truncate(formatMetric(primary_metric))}:{' '}
            {formatCell(primary_metric, visual[primary_metric], visual)}
          </span>
        }
        tooltip={getTooltip('primary_metric_quintile', visual, primary_metric)}
        scoreDisplay={`${primary_metric_quintile}/5`}
        score={primary_metric_quintile / 5}
        disable={!columns?.includes('primary_metric_quintile')}
      />
      <ScoreSection
        title={<p>Spend Quintile </p>}
        subtitle={
          <span style={{ color: '#7a85a3' }}>
            Spend: {toCurrency(visual.spend)} |{' '}
            {toPercent_2(visual.percentSpend ?? 0)} Budget
          </span>
        }
        tooltip={getTooltip('spend_quintile', visual, primary_metric)}
        scoreDisplay={`${spend_quintile}/5`}
        score={spend_quintile / 5}
        disable={!columns?.includes('spend_quintile') || isOrganicView}
      />
    </div>
  );
};

const ScoreSection = ({
  title,
  subtitle,
  tooltip,
  scoreDisplay,
  score,
  disable = false,
}) => {
  if (disable) return null;

  let color = '';
  const percent = score * 100;

  if (percent === 100) {
    color = '#0FBDC3';
  } else if (percent >= 75) {
    color = '#78E154';
  } else if (percent >= 40) {
    color = '#FFB543';
  } else if (percent >= 40) {
    color = '#FF7D54';
  } else {
    color = '#FF3096';
  }

  return (
    <Tooltip shouldShow={!!tooltip} content={tooltip}>
      <div className="score-card">
        <div className="d-flex w-100 justify-content-between">
          {title}
          <p>{scoreDisplay}</p>
        </div>
        {subtitle}

        <div className="score-bar">
          <div
            style={{
              width: `${percent}%`,
              background: color,
            }}
            className={`score-bar__fill`}
          />
        </div>
      </div>
    </Tooltip>
  );
};

const isScoreField = (field) =>
  ['fitness_score', 'primary_metric_quintile', 'spend_quintile'].includes(
    field
  );

const MetricData = (props) => {
  return (
    <>
      <div className="mb-3">
        <Data {...props} />
      </div>
      {/* <Attributes isDataTab={true} {...props} /> */}
    </>
  );
};

function adjustColumns(metricsArray) {
  const validPrefixes = ['median', 'average', 'aggregate', 'total'];

  return metricsArray.map((metric) => {
    if (metric === 'total_link_clicks') {
      return 'clicks';
    }
    const parts = metric.split('_');
    const prefix = parts[0];
    if (validPrefixes.includes(prefix)) {
      return parts.slice(1).join('_');
    }
    return metric;
  });
}

const getSign = (isPositive, preferLower) => {
  if (isPositive) {
    if (preferLower) {
      return '-';
    }

    return '+';
  } else {
    if (preferLower) {
      return '+';
    }

    return '-';
  }
};

const Data = ({
  visual,
  metadataFields,
  columns,
  formatMetric,
  columnOptions = [],
}) => {
  const cols = columnOptions?.reduce((acc, { isMetadata, label, value }) => {
    if (
      !isMetadata &&
      !isScoreField(value) &&
      adjustColumns(columns).includes(value)
    ) {
      acc.push(<DataItem {...{ visual, label, value }} />);
    }

    return acc;
  }, []);

  if (cols.length % 2 !== 0) cols.push(<li></li>);

  return (
    <div className="dna-creative-data dna-creative-data--grid">
      <ul>{cols}</ul>
    </div>
  );
};

const DataItem = ({ visual, label, value }) => {
  const { isStaticReport } = useCreativeAnalyticsGet();
  const filteredAllVisualsLabel = useAllVisualsLabel();

  const preferLower = value.slice(0, 2) === 'cp';

  const percentDiff = findPercentDifference(
    filteredAllVisualsLabel?.[`aggregate_${value}`],
    visual[value],
    preferLower
  );

  const isPositive = preferLower ? percentDiff < 0 : percentDiff > 0;
  const { trendData } = useCreativePerformanceState() ?? { trendData: null };
  const [ref, overflowActive] = useOverflowActive();

  return (
    <li className="data-item" key={`${visual._id}_${value}`}>
      <Tooltip disabled={!overflowActive} content={formatMetric(label)}>
        <p className="data-item__label" ref={ref}>
          {formatMetric(label)}
          {!isStaticReport && trendData?.status !== 'idle' && (
            <PeriodComparison
              previousPeriodData={trendData?.data?.[visual._id] ?? null}
              loadingPreviousPeriodData={trendData?.status?.loading}
              metric={value}
              {...{
                visual,
                formatMetric,
              }}
            />
          )}
        </p>
      </Tooltip>
      <p className="data-item__value">
        {formatCell(value, visual[value], visual)}{' '}
        {percentDiff && (
          <Tooltip
            content={`This visual is performing ${toPercent_2(
              Math.abs(percentDiff)
            )} ${
              isPositive ? 'better ' : 'worse '
            } than your average value of ${formatCell(
              value,
              filteredAllVisualsLabel[`aggregate_${value}`]
            )}`}
          >
            <span
              className="data-item__diff"
              style={{
                fontWeight: 400,
                padding: '0 3px',
                borderRadius: '25px',
                color: isPositive ? '#239264' : '#b42e56',
                background: isPositive ? '#d7ffee' : '#fde3eb',
                whiteSpace: 'nowrap',
              }}
            >
              {getSign(isPositive, preferLower)}
              {toPercent_2(Math.abs(percentDiff))}

              {/* <TooltipIcon
                iconStyle={{ marginLeft: '0.5rem' }}
                content={`This visual is performing ${toPercent_2(
                  Math.abs(percentDiff)
                )} ${
                  isPositive ? 'better ' : 'worse '
                } than your average value of ${formatCell(
                  value,
                  filteredAllVisualsLabel[`aggregate_${value}`]
                )}`}
              /> */}
            </span>
          </Tooltip>
        )}
      </p>
    </li>
  );
};

const Attributes = ({
  visual,
  metadataFields,
  columns,
  handleCreateManualTag,
  isDataTab = false,
  metricsLookup,
}) => {
  if (!metadataFields) {
    if (isDataTab) return null;
    return <p className="m-3">No category selected.</p>;
  }

  const cols = adjustColumns(columns).map((col) => {
    const match = [...(metricsLookup ?? [])].find(
      ([key, value]) => col === key
    );
    
    return match?.[1] || null;
  });

  let arr = metadataFields.reduce((result, field) => {
    if (cols.includes(field)) {
      result.push(
        <li className="data-item mb-3">
          <p className="data-item__label">{field}</p>
          <p className="data-item__value">{visual[field] ?? '-'}</p>
        </li>
      );
    }

    return result;
  }, []);

  if (!arr?.length) return <p className="m-3">No category selected.</p>;

  return (
    <div className="dna-creative-data">
      <ul>
        {arr}
        {!!visual?.manual_tags?.length && (
          <li className="data-item">
            <i
              style={{
                cursor: 'pointer',
              }}
              onClick={() => handleCreateManualTag(visual._id)}
              className="data-item__btn fa-regular fa-square-plus ml-3"
            ></i>
            <p className="data-item__label">Manual Tags</p>
            <p className="data-item__value">{visual.manual_tags.join(',')}</p>
          </li>
        )}
      </ul>
    </div>
  );
};

const InfoDisplay = ({ visual }) => {
  const { ad_names, id } = visual;

  return (
    <div style={{ fontSize: '13px' }} className="dna-creative-data p-3">
      <div className="data-item p-0  mb-3">
        <p className="data-item__label">Visual Hash</p>
        <p className="data-item__value">{id}</p>
      </div>

      <p className="mb-1">Visual is used in these ads:</p>
      <ul className="ad-names-list p-3 pt-1">
        {ad_names.map((ad_name) => (
          <li>
            <CopyableText>{ad_name}</CopyableText>
          </li>
        ))}
      </ul>
    </div>
  );
};
